import { Heart, Building2, Handshake, Brain, Leaf, Activity, Scale, Dumbbell, Baby, Zap, Star, Users, Shield, Sparkles, Stethoscope, Target, Award, TrendingUp, Clock, Globe, CheckCircle, Apple, Coffee, Salad, Utensils } from "lucide-react";
import { ServiceCard } from "./ServiceCard";
import { ServicesComparison } from "@/components/ServicesComparison";
import { useServicesContent } from "@/hooks/useServicesContent";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { settingsApi } from "@/integrations/mysql/client";
import { Skeleton } from "@/components/ui/skeleton";
import { useState, useEffect } from "react";

interface ServicesGridProps {
  onSchedule: (serviceType: string) => void;
}

const iconMap: Record<string, any> = {
  Heart, Building2, Handshake, Brain, Leaf, Activity, Scale, Dumbbell, Baby,
  Zap, Star, Users, Shield, Sparkles, Stethoscope, Target, Award, TrendingUp,
  Clock, Globe, CheckCircle, Apple, Coffee, Salad, Utensils,
};

const DEFAULT_COMPARISONS = [
  {
    title: "Acompanhamento Nutricional",
    description: "Plano personalizado para reeducação alimentar e mudança de hábitos",
    services: [
      { name: "Consulta inicial completa (90 min)", included: true },
      { name: "Anamnese alimentar detalhada", included: true },
      { name: "Avaliação antropométrica", included: true },
      { name: "Plano alimentar personalizado", included: true },
      { name: "Lista de substituições", included: true },
      { name: "Retorno em 30 dias", included: true },
      { name: "Suporte via WhatsApp", included: true },
      { name: "Receitas exclusivas", included: false },
      { name: "Acompanhamento semanal", included: false },
    ],
  },
  {
    title: "Personal Diet",
    description: "Acompanhamento intensivo com suporte contínuo",
    services: [
      { name: "Tudo do Acompanhamento Nutricional", included: true, highlight: true },
      { name: "Consultas semanais", included: true },
      { name: "Suporte diário via WhatsApp", included: true },
      { name: "Cardápio semanal detalhado", included: true },
      { name: "Receitas exclusivas", included: true },
      { name: "Orientação para compras", included: true },
      { name: "Ajustes em tempo real", included: true },
      { name: "Monitoramento diário", included: true },
      { name: "Relatórios de progresso", included: true },
    ],
  },
  {
    title: "Nutrição Esportiva",
    description: "Especializado para atletas e praticantes de atividade física",
    services: [
      { name: "Avaliação da composição corporal", included: true },
      { name: "Plano alimentar para treino", included: true },
      { name: "Estratégia de hidratação", included: true },
      { name: "Suplementação orientada", included: true },
      { name: "Periodização nutricional", included: true },
      { name: "Acompanhamento de performance", included: true },
      { name: "Orientação pré e pós-treino", included: true },
      { name: "Plano para competições", included: false },
      { name: "Acompanhamento presencial", included: false },
    ],
  },
  {
    title: "Reeducação Alimentar",
    description: "Foco na mudança gradual e sustentável de hábitos",
    services: [
      { name: "Identificação de padrões alimentares", included: true },
      { name: "Educação nutricional", included: true },
      { name: "Estratégias comportamentais", included: true },
      { name: "Plano de transição gradual", included: true },
      { name: "Material educativo", included: true },
      { name: "Acompanhamento psicológico", included: false },
      { name: "Grupos de apoio", included: false },
      { name: "Oficinas culinárias", included: false },
      { name: "Suporte familiar", included: false },
    ],
  },
];

export function ServicesGrid({ onSchedule }: ServicesGridProps) {
  const { content: mainServices, loading: servicesLoading } = useServicesContent("main_services");
  const { getSetting, isLoading: settingsLoading } = useSiteSettings();
  const [serviceComparisons, setServiceComparisons] = useState(DEFAULT_COMPARISONS);

  useEffect(() => {
    settingsApi.list().then((list: any[]) => {
      const raw = list.find((s: any) => s.key === "service_comparisons")?.value;
      if (raw) {
        try { setServiceComparisons(JSON.parse(raw)); } catch {}
      }
    }).catch(() => {});
  }, []);

  const showComparisonTables =
    getSetting('show_comparison_tables', 'false') === 'true' ||
    getSetting('show_comparison_table',  'false') === 'true';

  const isLoading = servicesLoading || settingsLoading;

  return (
    <section className="py-16">
      <div className="container mx-auto px-6">
        {isLoading ? (
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-96 w-full" />
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {mainServices
              .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
              .map((service) => {
                const serviceData = service.content_value as any;
                const IconComponent = iconMap[serviceData.icon] || Heart;
                
                return (
                  <ServiceCard
                    key={service.id}
                    icon={IconComponent}
                    title={serviceData.title}
                    description={serviceData.description}
                    features={serviceData.features || []}
                    buttonText={serviceData.buttonText}
                    buttonUrl={serviceData.buttonUrl}
                    highlighted={serviceData.highlighted}
                    onSchedule={() => onSchedule(serviceData.serviceType)}
                  />
                );
              })}
          </div>
        )}

        {/* Services Comparison Tables - Conditional */}
        {showComparisonTables && (
          <div className="mt-20">
            {/* Section header */}
            <div className="text-center mb-10">
              <span className="inline-flex items-center gap-2 bg-brand-primary/8 text-brand-primary text-xs font-semibold px-4 py-1.5 rounded-full mb-4 uppercase tracking-wide">
                Comparativo
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 font-primary mb-3">
                O que está incluído em cada plano?
              </h2>
              <p className="text-gray-500 text-base max-w-xl mx-auto">
                Escolha o atendimento que melhor se encaixa nos seus objetivos e rotina.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              {serviceComparisons.map((comparison, index) => (
                <ServicesComparison
                  key={index}
                  title={comparison.title}
                  description={comparison.description}
                  services={comparison.services}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
